import React from "react";
import ReactDOM from "react-dom/client";
import {
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
import { App } from "./App";
import { PermissionRoute } from "./components/auth/PermissionRoute";
import { AuthSessionProvider, useAuthSession } from "./contexts/AuthSessionContext";
import { LoginPage } from "./pages/LoginPage";
import { RFPsUserListingPage } from "./pages/RFPsUserListingPage";
import { OktaCallbackPage } from "./pages/OktaCallbackPage";
import { LibraryPage } from "./pages/LibraryPage";
import { ContractsTemplatesSelectionPage } from "./pages/ContractsTemplatesSelectionPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { ContractListPage } from "./pages/ContractListPage";
import { ContractTemplateDetailPage } from "./pages/ContractTemplateDetailPage";
import { MyContractsPage } from "./pages/MyContractsPage";
import { RFPsPage } from "./pages/RFPsPage";
import { RFPQuestionDetailPage } from "./pages/RFPQuestionDetailPage";
import { RFPQuestionAnswersPage } from "./pages/RFPQuestionAnswersPage";
import { LegalEntityPage } from "./pages/LegalEntityPage";
import { RepresentativeDetailsPage } from "./pages/RepresentativeDetailsPage";
import { UserManagementPage } from "./pages/UserManagementPage";
import { AuthorizationManagementPage } from "./pages/AuthorizationManagementPage";
import { PrizeLogicPage } from "./pages/PrizeLogicPage";
import { GameRulesTemplatesSelectionPage } from "./pages/GameRulesTemplatesSelectionPage";
import { GameRuleLibraryTemplatesPage } from "./pages/GameRuleLibraryTemplatesPage";
import { GameRuleTemplateDetailPage } from "./pages/GameRuleTemplateDetailPage";
import { GameRuleKbSectionsPage } from "./pages/GameRuleKbSectionsPage";
import { GameRuleKbSectionDetailPage } from "./pages/GameRuleKbSectionDetailPage";
import { ProfilePage } from "./pages/ProfilePage";
import "./styles/tailwind.css";

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element with id 'root' not found");
}

const RequireAuth = ({ children }: { children: React.ReactElement }) => {
  const { isShellAuthenticated } = useAuthSession();
  return isShellAuthenticated ? children : <Navigate to="/login" replace />;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* Auth pages — standalone, no AppLayout */}
      <Route path="login" element={<LoginPage />} />
      <Route path="okta-callback" element={<OktaCallbackPage />} />

      {/* App pages — inside AppLayout */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <App />
          </RequireAuth>
        }
      >
        <Route
          path="library"
          element={
            <PermissionRoute requireLibrarySurface>
              <LibraryPage />
            </PermissionRoute>
          }
        />
        <Route
          path="contracts-selection"
          element={
            <PermissionRoute requireContractsLibraryAdmin>
              <ContractsTemplatesSelectionPage />
            </PermissionRoute>
          }
        />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="contracts" element={<ContractListPage />} />
        <Route path="contracts/:id" element={<ContractTemplateDetailPage />} />
        <Route index element={<Navigate to="/my-contracts" replace />} />
        <Route path="my-contracts" element={<MyContractsPage />} />
        <Route path="my-rfps" element={<RFPsUserListingPage />} />
        <Route path="my-rfps/rfps-question-answers/:rfpId" element={<RFPQuestionAnswersPage />} />
        <Route
          path="rfps"
          element={
            <PermissionRoute requireRfpLibraryAdmin>
              <RFPsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="rfps/rfp-question-detail"
          element={
            <PermissionRoute requireRfpLibraryAdmin>
              <RFPQuestionDetailPage />
            </PermissionRoute>
          }
        />
        <Route path="legal-entity" element={<LegalEntityPage />} />
        <Route path="representative-details" element={<RepresentativeDetailsPage />} />
        <Route
          path="user"
          element={
            <PermissionRoute requireSuperAdmin>
              <UserManagementPage />
            </PermissionRoute>
          }
        />
        <Route path="profile" element={<ProfilePage />} />
        <Route
          path="prizelogic"
          element={
            <PermissionRoute requirePrizeRulesAccess>
              <PrizeLogicPage />
            </PermissionRoute>
          }
        />
        <Route
          path="authorization-management"
          element={
            <PermissionRoute requireSuperAdmin>
              <AuthorizationManagementPage />
            </PermissionRoute>
          }
        />
        <Route
          path="rules-selection"
          element={
            <PermissionRoute requirePrizeRulesLibraryAdmin>
              <GameRulesTemplatesSelectionPage />
            </PermissionRoute>
          }
        />
        <Route path="rules/templates" element={<GameRuleLibraryTemplatesPage />} />
        <Route path="rules/templates/:templateId" element={<GameRuleTemplateDetailPage />} />
        <Route
          path="rules"
          element={
            <PermissionRoute requirePrizeRulesLibraryAdmin>
              <GameRuleKbSectionsPage />
            </PermissionRoute>
          }
        />
        <Route
          path="rules/sections/:sectionGroupId"
          element={
            <PermissionRoute requirePrizeRulesLibraryAdmin>
              <GameRuleKbSectionDetailPage />
            </PermissionRoute>
          }
        />
      </Route>
    </>,
  ),
);

const root = ReactDOM.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <AuthSessionProvider>
      <RouterProvider router={router} />
    </AuthSessionProvider>
  </React.StrictMode>,
);
